import {
  type MutationStatus,
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query';
import { t } from 'i18next';
import { useEffect } from 'react';
import { useHandlers, useSite } from '../../../context/page-context';
import { useTimeout } from '../../../hooks/use-timeout';
import { track } from '../../../lib/mixpanel';
import { useBillingApi } from './use-billing-api';

/**
 * Time to wait after the API returns a redirect URL before we assume the
 * top-level navigation has failed and surface an error to the user. The
 * browser should be tearing the page down well before this fires; if it
 * hasn't, something has gone wrong.
 */
const REDIRECT_TIMEOUT_MS = 10_000;

const INITIATE_PLAN_MUTATION_KEY_PREFIX = ['billing', 'initiate-plan'] as const;

const initiatePlanMutationKey = (planKey: string) =>
  [...INITIATE_PLAN_MUTATION_KEY_PREFIX, planKey] as const;

type UseInitiateBillingPlanFlowOptions = {
  planKey: string;
  planName: string;
  planPrice: number | null;
};

/**
 * Kicks off the billing flow for a given plan key. On success the user is
 * redirected to the URL returned by the API (e.g. Shopify billing approval,
 * Stripe checkout, etc) via a top-level navigation.
 *
 * We cannot observe whether the redirect actually succeeded, so `isInitiating`
 * stays true from the moment the request fires until either the page unloads
 * or `REDIRECT_TIMEOUT_MS` elapses — at which point we reset state and notify
 * the user that the redirect failed.
 */
export function useInitiateBillingPlanFlow({
  planKey,
  planName,
  planPrice,
}: UseInitiateBillingPlanFlowOptions) {
  const { navigationHandler, notificationHandler } = useHandlers();
  const { billingApi } = useBillingApi();
  const site = useSite();
  const { siteId } = site;
  const queryClient = useQueryClient();

  const trackProps = {
    plan_key: planKey,
    plan_name: planName,
    plan_price: planPrice,
  };

  const { mutate, status, error, reset } = useMutation({
    mutationKey: initiatePlanMutationKey(planKey),
    mutationFn: () => billingApi.initiateFlow(siteId, planKey),
    onMutate: () => {
      track(site, 'Billing Plan Flow Initiated', trackProps);
    },
    onSuccess: ({ redirect_to }) => {
      track(site, 'Billing Plan Flow Redirecting', { ...trackProps, redirect_to });
      navigationHandler.navigate(redirect_to, {
        top: true,
        excludeSiteEnv: true,
        excludeSiteId: true,
      });
    },
    onError: (err) => {
      console.error('Failed to initiate billing plan flow:', err);
      track(site, 'Billing Plan Flow Failed', {
        ...trackProps,
        error_message: err instanceof Error ? err.message : String(err),
      });
      notificationHandler.error(
        t(
          'features:billing.hooks.use_initiate_billing_plan_flow.errors.initiation_failed_description',
        ),
      );
    },
  });

  const isInitiating = (status: MutationStatus) => status === 'pending' || status === 'success';

  // Other plan cards on the page need to know this card's flow is in progress
  // so they can disable themselves; we read sibling state from the mutation
  // cache (prefix-matching the shared key, excluding our own per-plan key).
  const otherPlanStatuses = useMutationState({
    filters: {
      mutationKey: INITIATE_PLAN_MUTATION_KEY_PREFIX,
      predicate: (mutation) =>
        (mutation.options.mutationKey as readonly unknown[])?.at(-1) !== planKey,
    },
    select: (mutation) => mutation.state.status,
  });

  const isOtherPlanInitiating = otherPlanStatuses.some(isInitiating);

  useTimeout(
    () => {
      track(site, 'Billing Plan Flow Redirect Timeout', {
        ...trackProps,
        timeout_ms: REDIRECT_TIMEOUT_MS,
      });
      notificationHandler.error(
        t(
          'features:billing.hooks.use_initiate_billing_plan_flow.errors.redirect_failed_description',
        ),
      );

      // `reset()` clears this card's local observer; `cache.remove()` clears
      // sibling cards observing via `useMutationState` (whose observers we
      // can't reach directly). We need both - completed mutations otherwise
      // linger in the cache for `gcTime`, leaving siblings stuck.
      reset();
      const mCache = queryClient.getMutationCache();
      for (const mutation of mCache.findAll({ mutationKey: INITIATE_PLAN_MUTATION_KEY_PREFIX })) {
        mCache.remove(mutation);
      }
    },
    status === 'success' ? REDIRECT_TIMEOUT_MS : null,
  );

  // If the user navigates away before the redirect fires (or before the
  // timeout elapses), the success-state mutation would otherwise linger in
  // the cache for `gcTime` and leave sibling cards stuck disabled on return.
  useEffect(() => {
    return () => {
      const mCache = queryClient.getMutationCache();
      for (const mutation of mCache.findAll({ mutationKey: initiatePlanMutationKey(planKey) })) {
        mCache.remove(mutation);
      }
    };
  }, [planKey, queryClient]);

  return {
    initiateBillingPlanFlow: mutate,
    isInitiating: isInitiating(status),
    isOtherPlanInitiating,
    error,
  };
}
